'use client';

import { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';


const heartLetters = [
{
  letter: 'H',
  word: 'Health',
  description:
  'Expanding access to early health education, preventive care, and wellness resources so every person can build a strong foundation for their future.',
  image: '/assets/images/Screenshot_2026-04-01_at_5.52.52_PM-1775080384360.png',
  alt: 'Person stretching and exercising representing health, wellness, and preventive care'
},
{
  letter: 'E',
  word: 'Education',
  description:
  'Ensuring children and communities have access to quality health education, nutrition knowledge, and the information needed to make empowered decisions.',
  image: '/assets/images/Food_For_Thought_Foundation_teaching_Kids_STEM_and_Building_Hydroponics-1774490919106.png',
  alt: 'Children learning STEM and building hydroponics through Food for Thought Foundation education program'
},
{
  letter: 'A',
  word: 'Access',
  description:
  'Advocating for affordable, transparent healthcare and treatment so that cost, location, and systemic barriers never stand between a person and the care they need.',
  image: '/assets/images/Screenshot_2026-04-01_at_5.49.14_PM-1775080210632.png',
  alt: 'Mary holding the Food for Thought Foundation sign representing access to health and opportunity'
},
{
  letter: 'R',
  word: 'Research',
  description:
  "Advancing solutions in women's health, medical access, and health equity through data-informed advocacy and partnerships with institutions driving change.",
  image: '/assets/images/Screenshot_2026-04-01_at_5.39.11_PM-1775079647678.png',
  alt: 'Two women posing in front of Harvard Medical School and Dana-Farber research posters representing health research and advocacy'
},
{
  letter: 'T',
  word: 'Togetherness',
  description:
  'Building strong communities through school engagement, outreach, and partnerships — because lasting health change happens when we move together.',
  image: '/assets/images/kiddos-1774394758172.png',
  alt: 'Children and community members coming together in a spirit of togetherness and shared purpose'
}];


const reasons = [
'over half of New York adults face healthcare affordability burdens, skipping care, delaying prescriptions, or struggling to pay medical bills.',
'children are growing up without consistent access to nutritious food, health education, and care, the very foundation of their future.',
'the top causes of death in New York are largely preventable through lifestyle changes.',
'in one of the wealthiest states in the world, your health is still determined by your zip code.',
"women's health is still under-researched, underfunded, and misunderstood.",
'life-saving medicine exists, but not everyone can access or afford it.',
"no one\'s dreams should be limited by their zip code or their health status."];


const pillars = [
{ icon: '🛡️', title: 'Prevention', desc: 'Expanding access to early health education and awareness', image: '', alt: 'Prevention - early health education and awareness' },
{ icon: '🏥', title: 'Access', desc: 'Advocating for affordable, transparent healthcare and treatment', image: '', alt: 'Access - affordable and transparent healthcare' },
{ icon: '🥦', title: 'Nutrition', desc: 'Addressing food insecurity as a core part of health', image: '', alt: 'Nutrition - addressing food insecurity' },
{ icon: '💡', title: 'Innovation', desc: "Advancing solutions in women\'s health and medical access", image: '', alt: "Innovation - advancing women\'s health solutions" }];


const problems = [
{
  number: '03',
  emoji: '🧾',
  title: 'Healthcare lacks transparency',
  quote: 'Access does not just mean availability. It means clarity.'
},
{
  number: '05',
  emoji: '👶',
  title: 'Children are at the center of this crisis',
  quote: 'When children lack access to health and nutrition, they lose access to opportunity.'
},
{
  number: '06',
  emoji: '🍎',
  title: 'Food insecurity is a healthcare crisis',
  quote: 'We cannot talk about healthcare without talking about what people are able to eat.'
},
{
  number: '07',
  emoji: '📍',
  title: 'Where you live determines your health',
  quote: 'In one of the wealthiest states, where you live determines how long you live.'
},
{
  number: '08',
  emoji: '🤰🧪',
  title: "Maternal and women\'s health are overlooked and under-resourced",
  quote: "A woman's life should not be at risk for bringing life into the world. We are still building healthcare systems that were never designed for women."
},
{
  number: '10',
  emoji: '🌍',
  title: 'Health inequity is a systemic issue',
  quote: 'Health equity is not a luxury. It is a prerequisite for justice.'
}];


const partnerships = [
  {
    icon: '🎓',
    name: 'New York State Department of Education',
    description: "Education is one of the most powerful tools for improving long-term health outcomes. Through partnerships with schools and educational leaders, I am working to expand the Food for Thought Foundation's School Health Hubs, bringing hydroponic growing systems, nutrition education, STEM learning, and fresh produce directly into schools and communities that need them most. These hubs empower students to become leaders in health, sustainability, and innovation while providing tangible resources to families.",
  },
  {
    icon: '🏥',
    name: 'DispatchHealth',
    description: "Healthcare should be accessible, affordable, and available when people need it most. DispatchHealth's innovative model of delivering same-day medical care directly to patients' homes aligns closely with my vision for the future of healthcare. As a healthcare innovator, I am passionate about advancing solutions that reduce unnecessary emergency room visits, increase access to care, and bring healthcare beyond hospital walls and into our communities.",
  },
  {
    icon: '💚',
    name: 'Kimber Health',
    description: 'Working to connect New Yorkers with resources that improve everyday health outcomes, including healthcare navigation, wellness opportunities, healthy food initiatives, and community-based support services. Through this partnership, we can help families access resources that contribute to healthier lives and stronger communities.',
  },
  {
    icon: '🎗️',
    name: 'American Cancer Society',
    description: 'Cancer advocacy is deeply personal to me. After witnessing the impact cancer had on my own family, I became committed to advancing awareness, research, and patient support. Through the American Cancer Society, I hope to help promote earlier detection, increased access to care, and continued investment in the development of faster, more effective, and more affordable cancer treatments and therapeutics.',
  },
  {
    icon: '❤️',
    name: 'American Heart Association',
    description: "As the Miss America Organization's national philanthropic partner, the American Heart Association aligns directly with my commitment to preventive healthcare. Through heart health education, CPR and AED awareness initiatives, nutrition advocacy, and active living programs, I aim to empower New Yorkers to take proactive steps toward better health before a crisis ever occurs.",
  },
];


export default function BoldThroughHeartPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Header />
      <main className="bg-white">

        {/* ── HERO ── */}
        <section className="pt-16">
          {/* Text content with photo as background */}
          <div
            className="relative w-full flex flex-col items-center text-center gap-4 px-6 py-20"
            style={{
              backgroundImage: "url('/assets/images/Mary_1_-1775078367745.png')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat"
            }}>

            {/* Overlay for readability */}
            <div className="absolute inset-0 bg-black/50" />
            <p className="relative z-10 text-white/80 tracking-widest text-xs uppercase">Community Service Initiative</p>
            <h1 className="relative z-10 text-5xl md:text-7xl font-bold text-white leading-tight tracking-tight">
              Lead With H.E.A.R.T.
            </h1>
            <p className="relative z-10 text-white/90 text-xl md:text-2xl font-light max-w-2xl">
              My commitment to a healthier, more equitable New York — and a future where no dream is limited by a zip code.
            </p>
          </div>
        </section>

        {/* ── WHITE MARY BANNER ── */}
        <section>
          <img
            src="/assets/images/Mary_2_-1775078708515.png"
            alt="Mary Bellakbira in green top with Miss Heart of New York title banner"
            className="w-full object-cover" />

        </section>

        {/* ── H.E.A.R.T. ACRONYM ── */}
        <section className="py-24 px-6 bg-muted/30">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <p className="text-xs text-foreground/40 tracking-widest uppercase mb-4">The Framework</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">What H.E.A.R.T. Stands For

              </h2>
              <p className="text-foreground/60 text-lg font-light max-w-2xl mx-auto">
                Every letter is a pillar. Every pillar is a promise.
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              {heartLetters?.map((item) =>
              <div
                key={item?.letter}
                className="flex-1 flex flex-col rounded-3xl bg-white border border-foreground/8 overflow-hidden hover:shadow-xl transition-shadow duration-300">

                  {/* Photo */}
                  <div className="relative h-56 flex-shrink-0">
                    <img
                    src={item?.image}
                    alt={item?.alt}
                    className="w-full h-full object-cover" />

                    {/* Letter badge overlay */}
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-foreground text-background flex items-center justify-center text-xl font-bold shadow-lg">
                      {item?.letter}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex flex-col justify-start p-6">
                    <p className="text-xs text-foreground/40 tracking-widest uppercase mb-1">
                      {item?.letter} stands for
                    </p>
                    <h3 className="text-xl font-bold text-foreground mb-3">{item?.word}</h3>
                    <p className="text-foreground/65 text-sm leading-relaxed font-light">{item?.description}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── WHY DO I ADVOCATE ── */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-xs text-foreground/40 tracking-widest uppercase mb-3">The Why</p>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Why Do I Advocate for a Healthier New York?
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {reasons?.slice(0, 6)?.map((reason, i) =>
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl border border-foreground/8 hover:border-foreground/20 hover:bg-muted/20 transition-all duration-200">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-bold mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-foreground/70 text-sm leading-relaxed">
                    <span className="font-semibold text-foreground">Because </span>{reason}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-3 col-span-full text-center px-4 py-10">
              <p className="text-2xl md:text-4xl font-bold text-foreground leading-snug">
                Because no one&apos;s dreams should be limited by their zip code or their health status.
              </p>
            </div>
          </div>
        </section>

        {/* ── COMMUNITY ENGAGEMENT & ADVOCACY ── */}
        <section className="py-24 px-6 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <p className="text-xs text-foreground/40 tracking-widest uppercase mb-4">Service in Action</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Community Engagement &amp; Advocacy</h2>
              <p className="text-foreground/60 text-lg font-light">
                20+ Appearances | 8,000+ Miles | One Mission
              </p>
            </div>

            {/* Text + Side Photo */}
            <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Text Content */}
              <div className="space-y-5 text-foreground/70 text-base leading-relaxed">
                <p>
                  As Miss Heart of New York, I believe representation begins with showing up.
                </p>
                <p>
                  Over the course of my year of service, I have traveled more than <span className="font-semibold text-foreground">8,000 miles</span> across New York, engaging with communities from classrooms and community centers to healthcare events, nonprofit initiatives, and public service programs. Whether implementing Food for Thought Foundation STEM and nutrition curricula, speaking on preventive healthcare, volunteering at community events, advocating for cancer awareness, or performing the National Anthem at civic celebrations, my goal has always been the same: to serve people where they are.
                </p>
                <p>
                  New York is one of the most diverse, resilient, innovative, and vibrant states in the nation. It deserves a representative who is not only visible on stage, but present in its communities — listening, learning, and leading alongside the people it serves.
                </p>
              </div>

              {/* Side Photo Placeholder */}
              <div className="flex flex-col gap-3">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden">
                  <img
                    src="/assets/images/15-1780837489004.JPEG"
                    alt="Mary Bellakbira in a red gown at a United Way community engagement event representing Miss Heart of New York"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Appearances Photo Grid */}
            <div>
              <h3 className="text-xl font-bold text-foreground mb-6 text-center">Appearances Across New York</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Real appearance photo */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/15-1780837489004.JPEG"
                    alt="Mary Bellakbira in a red gown at a United Way community engagement event representing Miss Heart of New York"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Hurley Town appearance photo */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_9.12.47_AM-1780837984850.png"
                    alt="Mary Bellakbira in a white lace dress holding flowers at Hurley Town appearance as Miss Heart of New York"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Women balloon backdrop appearance photo */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_9.14.40_AM-1780838108714.png"
                    alt="Mary Bellakbira with four women in front of a Women balloon backdrop at a New York appearance as Miss Heart of New York"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Red hoodie with Miss Heart of New York sash on stool */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_9.21.49_AM-1780838530730.png"
                    alt="Mary Bellakbira in a red hoodie wearing her Miss Heart of New York sash, sitting on a stool at a New York appearance"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Black dress with Miss Heart of New York sash in front of The Floating Hospital backdrop */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_9.22.26_AM-1780838552238.png"
                    alt="Mary Bellakbira in a black dress wearing her Miss Heart of New York sash in front of The Floating Hospital backdrop"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Miss America contestants with sashes */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_9.24.11_AM-1780838672716.png"
                    alt="Mary Bellakbira with fellow Miss America contestants wearing their state sashes at a pageant appearance"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* White dress stone archway appearance photo */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/Screenshot_2026-06-07_at_9.27.08_AM-1780838839697.png"
                    alt="Mary Bellakbira in a white dress wearing her Miss Heart of New York sash standing in front of a stone archway"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Mary with firefighters and Miss New York contestants in front of fire truck */}
                <div className="aspect-square rounded-2xl overflow-hidden">
                  <img
                    src="/assets/images/IMG_7967-1780839281831.jpeg"
                    alt="Mary Bellakbira with firefighters and fellow Miss New York contestants in front of a fire truck at a New York appearance"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STRATEGIC PARTNERSHIPS & IMPACT ── */}
        <section className="py-24 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <p className="text-xs text-foreground/40 tracking-widest uppercase mb-4">Collaboration &amp; Change</p>
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">Strategic Partnerships &amp; Impact</h2>
              <p className="text-foreground/65 text-lg leading-relaxed max-w-3xl mx-auto">
                Through <span className="font-semibold text-foreground">Lead with H.E.A.R.T.</span> (Health, Education, Access, Research, and Togetherness), I am building partnerships that expand access to healthcare, wellness resources, and preventive education throughout New York.
              </p>
            </div>

            {/* Partnership Accordion */}
            <div className="space-y-4">
              {partnerships.map((partner, index) => (
                <div
                  key={index}
                  className="rounded-3xl border border-foreground/10 bg-muted/20 overflow-hidden transition-shadow duration-300 hover:shadow-lg"
                >
                  <button
                    onClick={() => toggleAccordion(index)}
                    className="w-full flex items-center gap-5 p-6 text-left focus:outline-none"
                    aria-expanded={openIndex === index}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-background flex items-center justify-center text-lg">
                      {partner.icon}
                    </div>
                    <h3 className="flex-1 text-xl font-bold text-foreground">{partner.name}</h3>
                    <div
                      className="flex-shrink-0 w-8 h-8 rounded-full border border-foreground/20 flex items-center justify-center transition-transform duration-300"
                      style={{ transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{ maxHeight: openIndex === index ? '600px' : '0px' }}
                  >
                    <div className="px-6 pb-6 pl-[5.25rem]">
                      <p className="text-foreground/65 leading-relaxed">{partner.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Building a Healthier New York */}
            <div className="mt-16 rounded-3xl bg-foreground text-background p-10 text-center">
              <h3 className="text-2xl md:text-3xl font-bold mb-5">Building a Healthier New York</h3>
              <p className="text-background/80 leading-relaxed text-lg mb-4">
                Together, these partnerships support a common vision: a New York where healthcare is more accessible, education creates opportunity, preventive care is prioritized, and every community has the resources needed to thrive.
              </p>
              <p className="text-background/70 leading-relaxed">
                From school health hubs and nutrition education to at-home healthcare services and disease prevention initiatives, my goal is to create sustainable, measurable impact that extends far beyond my year of service as Miss Heart of New York.
              </p>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );

}